import React from "react";
import { Button, Popover, Tag } from "antd";

import { PopoverText } from "@/components";
import OverflowPopover from "@/components/OverflowPopover";
import {
    PickListExportItem,
    PickListExportTask,
    PickListMergeRuleConfig,
    ProductMergeRule,
    SkuMergeRule,
} from "@/services/api/moreFeatures/pickList";
import { isEmpty } from "@/utils";
import PickListExcludeTypeNameMap from "../../map/PickListExcludeTypeNameMap";
import { productMergeRuleNameMap, skuMergeRuleNameMap } from "../../map/MergeRuleNameMap";
import ExportItemCheckboxGroup from "../ExportItemCheckboxGroup";
import "./index.less";

const REGEX_TAG = "[任意字符]";

interface TaskInfoItemProps {
    label: string;
    children: React.ReactNode;
}

export const TaskInfoItem: React.FC<TaskInfoItemProps> = ({ label, children }) => {
    return (
        <div className="pick-list-download-task-info-item">
            <span className="pick-list-download-task-info-label">{label}：</span>
            <div className="pick-list-download-task-info-content">{children}</div>
        </div>
    );
};

const parseSkuPropertiesFilterKeywords = (value?: string) => {
    return value?.split(/\r?\n/).filter((keyword) => keyword.trim()) ?? [];
};

const SkuPropertiesFilterKeywords: React.FC<{ value?: string }> = ({ value }) => {
    const keywords = parseSkuPropertiesFilterKeywords(value);

    return (
        <div className="pick-list-download-filter-keywords">
            {keywords.length
                ? keywords.map((keyword, keywordIndex) => {
                      const parts = keyword.split(REGEX_TAG);

                      return (
                          <div className="pick-list-download-filter-keyword" key={`${keywordIndex}-${keyword}`}>
                              {parts.map((part, partIndex) => (
                                  <React.Fragment key={`${keywordIndex}-${partIndex}`}>
                                      {part}
                                      {partIndex < parts.length - 1 ? (
                                          <Tag variant="outlined">任意字符</Tag>
                                      ) : null}
                                  </React.Fragment>
                              ))}
                          </div>
                      );
                  })
                : "暂无过滤词"}
        </div>
    );
};

const MergeRule: React.FC<{ value?: PickListMergeRuleConfig; showDefault?: boolean }> = ({
    value,
    showDefault,
}) => {
    if (!value) return showDefault ? <>默认</> : null;

    const { needIsolateByShop, productMergeRule, skuMergeRule } = value;
    const newMode = isEmpty(needIsolateByShop);
    const mergeType = !newMode
        ? needIsolateByShop
            ? "单店铺商品合并"
            : "多店铺商品合并"
        : productMergeRule === ProductMergeRule.SKU_RULE_ONLY
        ? "同款SKU合并统计"
        : "同款商品且同款SKU合并统计";
    const productMergeRuleText =
        productMergeRule === ProductMergeRule.SKU_RULE_ONLY ? null : productMergeRuleNameMap[productMergeRule];
    const defaultSkuMergeRule = skuMergeRule === SkuMergeRule.NAME_WITH_ORIGINAL_ID_OR_EXTERNAL_ID;
    const skuMergeRuleText = defaultSkuMergeRule ? "默认" : skuMergeRuleNameMap[skuMergeRule];
    const mergeRuleText = [mergeType, productMergeRuleText, skuMergeRuleText].filter(Boolean).join(" / ");

    return (
        <OverflowPopover content={mergeRuleText} maxWidth={420}>
            {productMergeRule === ProductMergeRule.SKU_RULE_ONLY ? (
                <PopoverText content="同款SKU合并统计仅按SKU维度展示统计数量，不展示商品维度基本信息">
                    {mergeType}
                </PopoverText>
            ) : (
                mergeType
            )}
            {productMergeRuleText ? <span> / {productMergeRuleText}</span> : null}
            <span> / </span>
            {defaultSkuMergeRule ? (
                <PopoverText
                    content={
                        <div>
                            <div>默认:① 单品名称一致且单品的ID相同 ② 单品名称一致且单品编码相同且不为空</div>
                            <div>①和②只要满足1个就会合并</div>
                        </div>
                    }
                >
                    {skuMergeRuleText}
                </PopoverText>
            ) : (
                skuMergeRuleText
            )}
        </OverflowPopover>
    );
};

interface TaskSettingsProps {
    excludeTypes?: PickListExportTask["excludeTypes"];
    exportItems?: PickListExportItem[];
    totalPickListExportItems?: PickListExportItem[];
    mergeRuleConfig?: PickListMergeRuleConfig;
    modifyProductQuantity?: boolean;
    skuPropertiesFilterKeywords?: string;
    /** 传入时展示当前开关状态；任务记录不传时保持原有展示逻辑 */
    autoFilterSkuProperties?: boolean;
    showDefaultMergeRule?: boolean;
}

const TaskSettings: React.FC<TaskSettingsProps> = (props) => {
    const {
        excludeTypes,
        exportItems,
        totalPickListExportItems,
        mergeRuleConfig,
        modifyProductQuantity,
        skuPropertiesFilterKeywords,
        autoFilterSkuProperties,
        showDefaultMergeRule,
    } = props;
    const excludeOrders = excludeTypes?.map((type) => PickListExcludeTypeNameMap[type]).join("，");
    const filterKeywords = parseSkuPropertiesFilterKeywords(skuPropertiesFilterKeywords);
    const shouldShowAutoFilterSetting = autoFilterSkuProperties !== undefined || filterKeywords.length > 0;

    return (
        <div className="pick-list-download-task-info">
            {excludeTypes?.length ? (
                <TaskInfoItem label="排除订单">
                    <OverflowPopover content={excludeOrders} maxWidth={420}>
                        {excludeOrders}
                    </OverflowPopover>
                </TaskInfoItem>
            ) : null}
            {mergeRuleConfig || showDefaultMergeRule ? (
                <TaskInfoItem label="合并统计设置">
                    <MergeRule value={mergeRuleConfig} showDefault={showDefaultMergeRule} />
                </TaskInfoItem>
            ) : null}
            {modifyProductQuantity ? <TaskInfoItem label="是否修改数量">是</TaskInfoItem> : null}
            {shouldShowAutoFilterSetting ? (
                <TaskInfoItem label="其他设置">
                    {autoFilterSkuProperties === false ? (
                        "未开启"
                    ) : (
                        <Popover
                            title="单品属性过滤词"
                            trigger="click"
                            placement="top"
                            classNames={{ root: "pick-list-download-filter-popover" }}
                            content={<SkuPropertiesFilterKeywords value={skuPropertiesFilterKeywords} />}
                        >
                            <Button type="link" size="small" className="pick-list-download-link">
                                查看过滤词（{filterKeywords.length}）
                            </Button>
                        </Popover>
                    )}
                </TaskInfoItem>
            ) : null}
            <TaskInfoItem label="导出信息">
                <Popover
                    trigger="click"
                    placement="top"
                    getPopupContainer={(triggerNode) => {
                        return (triggerNode.closest(".pick-list-export-container") as HTMLElement) || document.body;
                    }}
                    content={
                        <div
                            className="pick-list-task-settings-export-info"
                            style={{ pointerEvents: "none", cursor: "not-allowed" }}
                        >
                            <ExportItemCheckboxGroup
                                value={exportItems}
                                totalPickListExportItems={totalPickListExportItems}
                                editable={false}
                            />
                        </div>
                    }
                >
                    <Button type="link" size="small" className="pick-list-download-link">
                        查看导出信息
                    </Button>
                </Popover>
            </TaskInfoItem>
        </div>
    );
};

export default React.memo(TaskSettings);
